import { useEffect, useState } from 'react'

import CardSurface from '../card-surface/CardSurface'
import Button from '../button/Button'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { useResidentMessagesUnread } from '../../_hooks/use-resident-messages-unread'
import { ResidentMessage } from '../../_types/resident'
import {
  acknowledgeWarning,
  getMyMessages,
  markMyThreadRead,
  sendMyMessage,
} from '../../_data/resident'
import {
  AckNote,
  AckRow,
  AttachmentRow,
  AttachmentThumb,
  Bubble,
  EmptyText,
  Heading,
  MessageMeta,
  MessageRow,
  MessageStack,
  MessageText,
  NewBadge,
  ReplyInput,
  ReplyRow,
  RetractedNote,
  RetractedText,
  ThreadList,
  WarningBadge,
} from './styled'

interface MessagesCardProps {
  tenancyId: string
}

const formatWhen = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
  })

/**
 * The resident's messages card — their direct thread with the property manager,
 * including warnings (highlighted, with their escalation level + an Acknowledge
 * action). Unread PM messages are flagged and marked read on open; the resident
 * can also start the conversation. Always shown for a claimed tenancy.
 *
 * @param {MessagesCardProps} props - the resident's tenancy.
 * @returns {JSX.Element | null}
 */
const MessagesCard = ({ tenancyId }: MessagesCardProps): JSX.Element => {
  const { profile } = useQuickPassProfile()
  const { refresh: refreshUnreadBadge } = useResidentMessagesUnread()
  const [messages, setMessages] = useState<ResidentMessage[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [replyText, setReplyText] = useState<string>('')
  const [isSending, setIsSending] = useState<boolean>(false)

  const senderName =
    profile !== null
      ? `${profile.firstName} ${profile.lastName}`.trim()
      : 'Resident'

  const loadMessages = (): void => {
    void getMyMessages(tenancyId).then(loaded => {
      setMessages(loaded)
      const unread = loaded.filter(
        message =>
          message.senderRole === 'pm' &&
          message.readByRecipientAt === null &&
          message.voidedAt === null,
      ).length
      setUnreadCount(unread)
      if (unread > 0) {
        void markMyThreadRead(tenancyId).then(() => {
          void refreshUnreadBadge()
        })
      }
    })
  }

  useEffect(() => {
    loadMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenancyId])

  const handleSend = (): void => {
    if (replyText.trim() === '' || isSending === true) {
      return
    }
    setIsSending(true)
    void sendMyMessage(tenancyId, replyText.trim(), senderName).then(created => {
      setIsSending(false)
      if (created === null) {
        return
      }
      setMessages(previous => [...previous, created])
      setReplyText('')
    })
  }

  const handleAcknowledge = (messageId: string): void => {
    void acknowledgeWarning(tenancyId, messageId).then(updated => {
      if (updated === null) {
        return
      }
      setMessages(previous =>
        previous.map(message => (message.id === messageId ? updated : message)),
      )
    })
  }

  const isUnread = (message: ResidentMessage): boolean =>
    message.senderRole === 'pm' &&
    message.readByRecipientAt === null &&
    message.voidedAt === null

  const warningOrdinals = ((): Record<string, number> => {
    const ordinals: Record<string, number> = {}
    const counters: Record<string, number> = {}
    const liveWarnings = messages
      .filter(
        message => message.kind === 'warning' && message.voidedAt === null,
      )
      .sort((first, second) => first.createdAt - second.createdAt)
    liveWarnings.forEach(message => {
      const next = (counters[message.warningCategory] ?? 0) + 1
      counters[message.warningCategory] = next
      ordinals[message.id] = next
    })
    return ordinals
  })()

  const renderAttachments = (
    message: ResidentMessage,
  ): JSX.Element | null => {
    const urls = message.imageUrls ?? []
    if (urls.length === 0) {
      return null
    }
    return (
      <AttachmentRow>
        {urls.map(url => (
          <AttachmentThumb
            key={url}
            src={url}
            alt="Message attachment"
            onClick={() => window.open(url, '_blank')}
          />
        ))}
      </AttachmentRow>
    )
  }

  const renderMessage = (message: ResidentMessage): JSX.Element => {
    if ((message.voidedAt ?? null) !== null) {
      return (
        <MessageRow key={message.id} align="start">
          <Bubble tone="retracted">
            <RetractedText>{message.body}</RetractedText>
            <RetractedNote>
              {message.voidedReason !== ''
                ? `Retracted by your property manager — ${message.voidedReason}`
                : 'Retracted by your property manager'}
            </RetractedNote>
          </Bubble>
        </MessageRow>
      )
    }

    if (message.kind === 'warning') {
      const ordinal = warningOrdinals[message.id] ?? message.warningLevel
      return (
        <MessageRow key={message.id} align="start">
          <Bubble tone="warning">
            <WarningBadge>
              {message.warningCategory} · Warning {ordinal} of{' '}
              {message.warningMax}
            </WarningBadge>
            <MessageText>{message.body}</MessageText>
            {renderAttachments(message)}
            <MessageMeta>{formatWhen(message.createdAt)}</MessageMeta>
            <AckRow>
              {message.acknowledgedAt !== null ? (
                <AckNote>
                  Acknowledged {formatWhen(message.acknowledgedAt)}
                </AckNote>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={() => handleAcknowledge(message.id)}
                >
                  I acknowledge this
                </Button>
              )}
            </AckRow>
          </Bubble>
        </MessageRow>
      )
    }

    const isResident = message.senderRole === 'resident'
    return (
      <MessageRow key={message.id} align={isResident ? 'end' : 'start'}>
        <Bubble tone={isResident ? 'resident' : 'pm'}>
          {isUnread(message) === true ? <NewBadge>New</NewBadge> : null}
          <MessageText>{message.body}</MessageText>
          {renderAttachments(message)}
          <MessageMeta>
            {message.senderName} · {formatWhen(message.createdAt)}
          </MessageMeta>
        </Bubble>
      </MessageRow>
    )
  }

  return (
    <CardSurface padding="standard">
      <MessageStack>
        <Heading>
          Messages from your property manager
          {unreadCount > 0 ? ` (${unreadCount} new)` : ''}
        </Heading>

        {messages.length === 0 ? (
          <EmptyText>
            No messages yet — send your property manager a message below.
          </EmptyText>
        ) : (
          <ThreadList>
            {messages.map(message => renderMessage(message))}
          </ThreadList>
        )}

        <ReplyRow>
          <ReplyInput
            value={replyText}
            placeholder="Write a message…"
            onChange={event => setReplyText(event.target.value)}
          />
          <Button
            type="button"
            variant="primary"
            size="small"
            disabled={isSending === true || replyText.trim() === ''}
            onClick={handleSend}
          >
            Send
          </Button>
        </ReplyRow>
      </MessageStack>
    </CardSurface>
  )
}

export default MessagesCard
